// useSwipeToClose.js
// Custom hook for swipe-down-to-close functionality on mobile bottom sheets

import { useState, useCallback, useRef } from 'react';

/**
 * Hook for adding swipe-down-to-close functionality to bottom sheets
 * 
 * @param {Function} onClose - Function to call when sheet should close
 * @param {Object} options - Configuration options
 * @param {number} options.threshold - Minimum swipe distance to trigger close (default: 100)
 * @param {number} options.velocityThreshold - Minimum velocity to trigger close (default: 0.5)
 * @returns {Object} - handlers and state for the swipeable element
 */
export const useSwipeToClose = (onClose, options = {}) => {
  const { threshold = 100, velocityThreshold = 0.5 } = options;
  
  const [dragOffset, setDragOffset] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);
  const startTime = useRef(0);
  const currentY = useRef(0);

  const handleTouchStart = useCallback((e) => {
    // Only track touches on the drag handle area or top of sheet
    const touch = e.touches[0];
    startY.current = touch.clientY;
    currentY.current = touch.clientY;
    startTime.current = Date.now();
    setIsDragging(true);
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!isDragging) return;
    
    const touch = e.touches[0];
    currentY.current = touch.clientY;
    const deltaY = touch.clientY - startY.current;
    
    // Only allow downward drag (positive deltaY)
    if (deltaY > 0) {
      setDragOffset(deltaY);
      // Prevent scrolling while dragging down
      e.preventDefault();
    }
  }, [isDragging]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging) return;
    
    const deltaY = currentY.current - startY.current;
    const deltaTime = Date.now() - startTime.current;
    const velocity = deltaY / deltaTime; // pixels per millisecond
    
    // Close if dragged past threshold OR if velocity is high enough
    if (deltaY > threshold || (deltaY > 50 && velocity > velocityThreshold)) {
      onClose();
    }
    
    // Reset
    setDragOffset(0);
    setIsDragging(false);
  }, [isDragging, threshold, velocityThreshold, onClose]);

  // Style to apply to the sheet for drag animation
  const dragStyle = {
    transform: dragOffset > 0 ? `translateY(${dragOffset}px)` : undefined,
    transition: isDragging ? 'none' : 'transform 0.3s ease-out',
  };

  // Props to spread on the drag handle element
  const dragHandleProps = {
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
    style: { touchAction: 'none' },
  };

  // Props to spread on the sheet container
  const sheetProps = {
    style: dragStyle,
  };

  return {
    dragOffset,
    isDragging,
    dragHandleProps,
    sheetProps,
    // For manual control if needed
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  };
};

export default useSwipeToClose;